/**
 * StandardLessonActions
 * ──────────────────────────────────────────────────────────────────
 * The 5-button action bar that MUST appear at the bottom of every
 * lesson view across the platform (K-12 Academic, Courses,
 * Special Edition / Zion of Pillars / Forensic Etymology).
 *
 * Buttons (locked order):
 *   1. ← Back to Curriculum
 *   2. 🔄 Restart Lesson
 *   3. 📝 Take Lesson Quiz
 *   4. Next Lesson →           (rendered only if `onNextLesson` is provided)
 *   5. 📄 Download PDF
 *
 * Pass only the handlers you support — omitted buttons are hidden,
 * but if `onTakeQuiz` or `onDownloadPDF` is not provided the bar
 * still renders the four required core buttons so the lesson view
 * remains visually standardized.
 */
import React from "react";

export default function StandardLessonActions({
  onBack,
  onRestart,
  onTakeQuiz,
  onNextLesson,
  nextLessonLabel,
  onDownloadPDF,
  pdfLoading = false,
  align = "right",
}) {
  return (
    <div
      data-testid="standard-lesson-actions"
      style={{
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        padding: "20px 8px",
        borderTop: "1px solid #333",
        marginTop: "20px",
      }}
    >
      <button
        onClick={onBack}
        data-testid="back-to-curriculum-btn"
        style={{ background: "#333", color: "#fff", padding: "12px 25px", marginTop: 0, borderRadius: "8px", border: "1px solid #555", cursor: "pointer" }}
      >
        ← Back to Curriculum
      </button>

      <button
        onClick={onRestart}
        data-testid="restart-lesson-btn"
        title="Clear progress and start this lesson from the beginning"
        style={{
          background: "linear-gradient(135deg, #4a2a2a 0%, #6a3a3a 100%)",
          color: "#fff",
          padding: "12px 25px",
          fontWeight: "bold",
          marginTop: 0,
          borderRadius: "8px",
          border: "1px solid #FFA07A",
          cursor: "pointer",
        }}
      >
        🔄 Restart Lesson
      </button>

      {onTakeQuiz && (
        <button
          onClick={onTakeQuiz}
          data-testid="take-quiz-btn"
          style={{
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            color: "#000",
            padding: "12px 30px",
            fontWeight: "bold",
            marginTop: 0,
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
          }}
        >
          📝 Take Lesson Quiz
        </button>
      )}

      {onNextLesson && (
        <button
          onClick={onNextLesson}
          data-testid="next-lesson-btn"
          title={nextLessonLabel ? `Next: ${nextLessonLabel}` : "Go to the next lesson"}
          style={{
            background: "linear-gradient(135deg, #1a4d1a 0%, #2a6d2a 100%)",
            color: "#fff",
            padding: "12px 25px",
            fontWeight: "bold",
            marginTop: 0,
            borderRadius: "8px",
            border: "1px solid #90EE90",
            cursor: "pointer",
          }}
        >
          Next Lesson →
        </button>
      )}

      {onDownloadPDF && (
        <button
          onClick={onDownloadPDF}
          disabled={pdfLoading}
          data-testid="download-pdf-btn"
          title="Download this lesson as a printable PDF"
          style={{
            background: pdfLoading ? "#2a2a2a" : "linear-gradient(135deg, #3a3a1a 0%, #5a4a1a 100%)",
            color: pdfLoading ? "#888" : "#FFD700",
            padding: "12px 25px",
            fontWeight: "bold",
            marginTop: 0,
            borderRadius: "8px",
            border: "1px solid #FFD700",
            cursor: pdfLoading ? "wait" : "pointer",
          }}
        >
          {pdfLoading ? "Building PDF…" : "📄 Download PDF"}
        </button>
      )}
    </div>
  );
}
